import React, { useEffect, useState, useCallback, useRef } from 'react';
import { shipImg, LEGENDARY_NAMES } from '../lib';
import { shareUrl, shipText, summaryText, intentUrl } from '../share';

// Opening reveal (mystery box): each settled ship is a face-down card; flip one by one or "Reveal all".
// ships: [{ id, kind: 'Miner'|'Pirate', tier: 'Common'|'Rare'|'Epic'|'Legendary', serial }] read from Ships.getShip.
const nn = (n) => String(n).padStart(2, '0');

function LegendaryFlash({ ship, onDone, share, account }) {
  useEffect(() => { const t = setTimeout(onDone, 4500); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="lg-flash" onClick={onDone} data-testid="legendary-flash">
      <div className="lg-rays" />
      <div className="lg-content">
        <div className="lg-kicker">LEGENDARY {ship.kind.toUpperCase()}</div>
        <img className="lg-art" src={shipImg(ship)} alt={LEGENDARY_NAMES[ship.serial - 1]} />
        <div className="lg-serial">#{nn(ship.serial)}</div>
        <div className="lg-name">{LEGENDARY_NAMES[ship.serial - 1]}</div>
        <div className="lg-sub">1 of 20 · ship #{String(ship.id)} · click to continue</div>
        {share && <div style={{ marginTop: '1rem' }}><ShareBtn text={shipText(ship, account)} url={shareUrl(share, ship)} testid="share-flash">Share this Legendary on 𝕏</ShareBtn></div>}
      </div>
    </div>
  );
}

function ShareBtn({ text, url, testid, children }) {
  const href = intentUrl(text, url);
  return <a className="rv-share" href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} data-intent={href} onClick={(e) => e.stopPropagation()}>{children}</a>;
}

function RevealCard({ ship, flipped, active, onFlip, share, account }) {
  const t = ship.tier.toLowerCase();
  return (
    <div className={`rv-card ${flipped ? 'flipped' : ''} ${active && !flipped ? 'active' : ''}`} onClick={() => !flipped && onFlip()}
      data-testid={`reveal-card-${String(ship.id)}`}>
      <div className="rv-inner">
        <div className="rv-face rv-back">
          <div className="rv-logo">AF</div>
          <div className="rv-q">?</div>
          <div className="rv-hint">{active ? 'click to reveal' : 'ship #' + String(ship.id)}</div>
        </div>
        <div className={`rv-face rv-front rv-${t}`}>
          <div className={`tier tier-${t}`}>{ship.tier.toUpperCase()}</div>
          <img src={shipImg(ship)} alt={`${ship.tier} ${ship.kind}`} />
          <div className="rv-title">{ship.serial ? `#${nn(ship.serial)} ${LEGENDARY_NAMES[ship.serial - 1]}` : `${ship.kind}`}</div>
          <div className="rv-id">ship #{String(ship.id)}{ship.serial ? ` · ${ship.kind}` : ''}</div>
          {share && (ship.tier === 'Epic' || ship.tier === 'Legendary') && (
            <ShareBtn text={shipText(ship, account)} url={shareUrl(share, ship)} testid={`share-${String(ship.id)}`}>Share on 𝕏</ShareBtn>
          )}
        </div>
      </div>
    </div>
  );
}

// share: '/r/<pid>-<block>' (on-chain) or '/r/demo/<spec>' (preview) path for X share links; account adds the points ref code.
export default function Reveal({ ships, onClose, title = 'Your new ships', demo = false, share = null, account = null }) {
  const [flipped, setFlipped] = useState(() => ships.map(() => false));
  const [flash, setFlash] = useState(null); // legendary ship being celebrated
  const queue = useRef([]); // legendaries waiting for their flash (reveal all)
  const next = flipped.findIndex((f) => !f);
  const done = next === -1;

  const flip = useCallback((i) => {
    setFlipped((f) => { if (f[i]) return f; const g = [...f]; g[i] = true; return g; });
    if (ships[i].serial) setTimeout(() => setFlash(ships[i]), 650); // after the card turns
  }, [ships]);

  const revealAll = () => {
    const rest = ships.map((s, i) => i).filter((i) => !flipped[i]);
    rest.forEach((i, k) => setTimeout(() => setFlipped((f) => { const g = [...f]; g[i] = true; return g; }), k * 180));
    const lgs = rest.filter((i) => ships[i].serial).map((i) => ships[i]);
    if (lgs.length) setTimeout(() => { queue.current = lgs.slice(1); setFlash(lgs[0]); }, rest.length * 180 + 600);
  };
  const flashDone = useCallback(() => { setFlash(queue.current.shift() || null); }, []);

  useEffect(() => {
    const onKey = (e) => { if (flash) return; if ((e.key === ' ' || e.key === 'Enter') && !done) { e.preventDefault(); flip(next); } };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [flash, done, next, flip]);

  const counts = ['Common', 'Rare', 'Epic', 'Legendary'].map((t) => [t, ships.filter((s, i) => flipped[i] && s.tier === t).length]).filter(([, n]) => n);

  return (
    <div className="rv-overlay" data-testid="reveal">
      <div className="rv-panel">
        <div className="rv-head">
          <h2>{title}{demo && <span className="rv-demo">DEMO</span>}</h2>
          <div className="rv-actions">
            {!done && <button onClick={() => flip(next)} data-testid="reveal-next">Reveal next ({flipped.filter(Boolean).length + 1}/{ships.length})</button>}
            {!done && ships.length > 1 && <button className="rv-ghost" onClick={revealAll} data-testid="reveal-all">Reveal all</button>}
            {done && share && <ShareBtn text={summaryText(ships, account)} url={shareUrl(share)} testid="share-summary">Share results on 𝕏</ShareBtn>}
            {done && <button onClick={onClose} data-testid="reveal-close">Done</button>}
          </div>
        </div>
        <div className={`rv-grid n${Math.min(ships.length, 5)}`}>
          {ships.map((s, i) => <RevealCard key={String(s.id)} ship={s} flipped={flipped[i]} active={i === next} onFlip={() => flip(i)} share={share} account={account} />)}
        </div>
        <p className="rv-tally">{counts.length ? counts.map(([t, n]) => <span key={t} className={`tier-${t.toLowerCase()}`}>{n} {t}</span>) : 'Tiers are drawn on-chain with verifiable randomness (Chainlink VRF). Click a card or press Space to flip it.'}</p>
        {done && share && <p className="rv-tally" style={{ marginTop: '.4rem' }}>Shared? Paste your tweet on <a href="/points">/points</a> to earn share points (Gen1 priority later).</p>}
      </div>
      {flash && <LegendaryFlash ship={flash} onDone={flashDone} share={share} account={account} />}
    </div>
  );
}
